import { FC, HTMLAttributes } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { BottomPopup } from '@shared/components/BottomPopup';
import { Icon } from '@shared/components/icons';
import DatePickerModal from '@shared/containers/DatePicker/DatePickerModal';
import { feventTimeAtomFamily } from '@shared/containers/TimePicker/time.atom';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { FeventDate } from '@shared/types';
import { formatDate } from '@shared/utils/formatDate';
import { fromKstParts, toKstParts } from '@shared/utils/kstDate';
import { UseFormSetValue } from 'react-hook-form';
import { useId } from 'react-id-generator';

interface Props extends HTMLAttributes<HTMLDivElement> {
  name: string;
  fDate: FeventDate; // useWatch로 변화되는거 지켜보는 값, 이 값은 FeventDatePicker와 FeventTimePicker에서 변경 됨에 따라 같이 변경됨
  setValue: UseFormSetValue<any>;
  pickerId: string;
}

//* FeventDatePicker 날짜 선택 register의 해당 name의 맞는 setValue를 처리함
export const FeventDatePicker: FC<Props> = ({
  fDate,
  name,
  setValue,
  placeholder,
  pickerId,
  className = '',
  ...props
}: Props) => {
  const { CalendarPlaceHolder, Calendar, X } = Icon();
  const [id] = useId(1, 'datepicker');
  const openId = `datepicker-${id}`;
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(openId));

  //timePicker가 갖고 있는 time값
  const time = useRecoilValue(feventTimeAtomFamily(pickerId));

  //* date 초기화
  const onClear = () => {
    setValue(name, {
      date: null,
      timeDefined: fDate?.timeDefined ?? false,
    });
  };

  //* 기존 timeDefined에서 정의된 시간 값이 있으면 보존하도록함
  //* date는 DatePickerModal이 넘기는 KST 자정 순간값. 시간 합성도 KST 기준으로 고정.
  const onDatePickerModalValueChange = (date: Date | null) => {
    if (fDate?.timeDefined && time && date) {
      const d = toKstParts(date);
      const t = toKstParts(time);
      const tempDate = fromKstParts(d.year, d.month, d.date, t.hours, t.minutes);
      setValue(name, { date: tempDate, timeDefined: fDate.timeDefined });
    } else {
      setValue(name, { date, timeDefined: fDate?.timeDefined ?? false });
    }
  };

  return (
    <>
      <div
        className={`date-picker flex cursor-pointer justify-between px-1 theme-bg-1 theme-border-1 ${className}`}
        onClick={() => {
          setOpen(true);
        }}
        {...props}
      >
        {/* placeholder는 시간 칸과 같은 14px, 날짜 값은 좁은 폭에 맞춰 13px 유지.
            날짜+요일이 한 줄에 들어가도록 좌우 여백·아이콘을 줄임(글자 크기는 그대로) */}
        <div
          className={`flex min-w-0 flex-1 items-center ${
            fDate?.date ? 'text-13' : 'text-sm'
          }`}
        >
          {!fDate?.date ? (
            <CalendarPlaceHolder className="h-6 w-6 shrink-0 stroke-none" />
          ) : (
            <Calendar className="h-6 w-6 shrink-0 stroke-none" />
          )}
          <p
            className={`whitespace-nowrap ${
              !fDate?.date ? 'theme-text-6' : 'theme-text-1'
            }`}
          >
            {!fDate?.date
              ? placeholder
              : formatDate(fDate?.date, {
                  dateSeparater: '.',
                  contains: {
                    day: true,
                  },
                })}
          </p>
        </div>
        {fDate?.date && (
          <X
            onClick={(e) => {
              e.stopPropagation();
              //* 실수로 눌러 지워지지 않게 확인 후 삭제
              if (!window.confirm(`${placeholder}을 지울까요?`)) return;
              onClear();
            }}
            className="h-5 w-5 shrink-0 stroke-slate-400"
          />
        )}
      </div>
      <BottomPopup openId={openId}>
        <div className="w-screen bg-transparent">
          <DatePickerModal
            value={fDate?.date}
            onDateChange={onDatePickerModalValueChange}
            openId={openId}
            placeholder={placeholder}
          />
        </div>
      </BottomPopup>
    </>
  );
};
